import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";
import { RecognitionFilter } from "@shared/interfaces/RecognitionFilter.interface";

import { RecognitionStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const defaultFilter: RecognitionFilter = {
  page: 0,
  limit: 50,
  search: "",
};

export const initialState: RecognitionStateType = {
  recognition: null,
  receivedRecognitions: [],
  receivedRecognitionsTotal: 0,
  receivedRecognitionsIsLoaded: false,
  receivedRecognitionsLoadFailed: false,
  receivedRecognitionsLoadedPages: 0,
  sentRecognitions: [],
  sentRecognitionsTotal: 0,
  sentRecognitionsIsLoaded: false,
  sentRecognitionsLoadFailed: false,
  sentRecognitionsLoadedPages: 0,
  reviewRecognitions: [],
  reviewRecognitionsTotal: 0,
  reviewRecognitionsIsLoaded: false,
  coreValues: [],
  coreValuesLoadFailed: false,
  receivedFilter: { ...defaultFilter },
  sentFilter: { ...defaultFilter },
  reviewFilter: { ...defaultFilter },
  pendingCount: 0,
  recognitionCounts: { received: 0, sent: 0 },
};

const reducer = createReducer<RecognitionStateType, Action>(initialState)
  .handleAction(actions.getReceivedRecognitions.success, (state, action) =>
    produce(state, (nextState) => {
      const { rows, count, is_clear } = action.payload;
      if (is_clear) {
        nextState.receivedRecognitions = [...rows];
      } else {
        // De-dupe by id, mirroring getReviewRecognitions.success below: the api pages by OFFSET
        // (recognition.service.ts's .page()), so a row already held can come back in a later page
        // whenever the list shifts between two fetches -- e.g. createRecognition.success prepending
        // a new row to sentRecognitions moves every server offset for that list by one.
        // RecognitionListItem is keyed by id, so an un-de-duped repeat is a duplicate React key, not
        // just a visual repeat.
        //
        // Safe ONLY because receivedRecognitionsLoadedPages (below) advances once per landed page
        // regardless of how many rows survive de-duping -- it does NOT derive from rows.length. The
        // rejected row-derived cursor (web #68's Math.floor(rowCount / limit)) would stall here:
        // de-duping can make an append add fewer rows than a full page, and a row-count cursor would
        // then re-request the same page forever instead of advancing. If this cursor is ever changed
        // back to a row-derived form, this de-dupe turns from a fix into a stall.
        //
        // A de-duped row is deferred, not lost: receivedRecognitionsTotal (below) is always the
        // server's count, never this array's post-de-dupe length, so hasMore (rows.length < total)
        // keeps paging past this point and the row lands on a later page. It only goes unseen for a
        // viewer who stops scrolling before reaching that page -- this does not make the feed
        // gap-free, and only keyset pagination (not this change) removes the shifting-offset cause.
        const existingIds = new Set(nextState.receivedRecognitions.map((recognition) => recognition.id));
        nextState.receivedRecognitions = [
          ...nextState.receivedRecognitions,
          ...rows.filter((recognition) => !existingIds.has(recognition.id)),
        ];
      }
      nextState.receivedRecognitionsTotal = count;
      nextState.receivedRecognitionsIsLoaded = true;
      // review A: a retry after a failure clears the message instead of showing both a stale error
      // and the rows that just landed -- mirrors the sparks slice's own success/failure pair.
      nextState.receivedRecognitionsLoadFailed = false;
      // review B (C'): the tab's own landed-page counter, NOT derived from receivedRecognitions.length
      // -- a page-0 (is_clear) success always means exactly one page has now landed; any other success
      // means one more page landed than before, whatever its row count (a short page still advances
      // this, unlike the rejected rows.length-derived form).
      nextState.receivedRecognitionsLoadedPages = is_clear ? 1 : nextState.receivedRecognitionsLoadedPages + 1;
    }),
  )
  // feed-failed-page-plan.md: mirrors getReviewRecognitions.failure below verbatim -- only flip
  // isLoaded, do NOT touch receivedRecognitions/receivedRecognitionsTotal, so rows already held
  // survive a later failed refresh. Without this, a first load that fails leaves
  // receivedRecognitionsIsLoaded false forever and the container renders nothing at all.
  //
  // review A: also sets receivedRecognitionsLoadFailed, so the container can tell "loaded and
  // genuinely empty" apart from "failed to load" -- isLoaded alone cannot. review B (C'): deliberately
  // does NOT touch receivedRecognitionsLoadedPages -- a failed request added no page, so the counter
  // must not advance, which is what makes the next "Load more" click retry the SAME page instead of
  // skipping the one that failed.
  .handleAction(actions.getReceivedRecognitions.failure, (state) =>
    produce(state, (nextState) => {
      nextState.receivedRecognitionsIsLoaded = true;
      nextState.receivedRecognitionsLoadFailed = true;
    }),
  )
  .handleAction(actions.getSentRecognitions.success, (state, action) =>
    produce(state, (nextState) => {
      const { rows, count, is_clear } = action.payload;
      if (is_clear) {
        nextState.sentRecognitions = [...rows];
      } else {
        // Mirrors getReceivedRecognitions.success's own de-dupe above, for Sent's independent rows
        // and cursor: sentRecognitions is also paged by offset and also shifts at the front
        // (createRecognition.success prepends the giver's own new recognition here), so the same
        // overlap risk, the same loadedPages-only safety (a row-derived cursor would stall on a
        // de-duped-to-zero page), and the same deferred-not-lost caveat (a dropped duplicate is
        // still counted in sentRecognitionsTotal, so hasMore keeps paging until it lands later) all
        // apply to this list independently.
        const existingIds = new Set(nextState.sentRecognitions.map((recognition) => recognition.id));
        nextState.sentRecognitions = [
          ...nextState.sentRecognitions,
          ...rows.filter((recognition) => !existingIds.has(recognition.id)),
        ];
      }
      nextState.sentRecognitionsTotal = count;
      nextState.sentRecognitionsIsLoaded = true;
      // Mirrors getReceivedRecognitions.success's own reasoning above, for Sent's independent fields.
      nextState.sentRecognitionsLoadFailed = false;
      nextState.sentRecognitionsLoadedPages = is_clear ? 1 : nextState.sentRecognitionsLoadedPages + 1;
    }),
  )
  // feed-failed-page-plan.md: mirrors getReviewRecognitions.failure below verbatim -- same reasoning
  // as getReceivedRecognitions.failure above, for Sent's own independent fields.
  .handleAction(actions.getSentRecognitions.failure, (state) =>
    produce(state, (nextState) => {
      nextState.sentRecognitionsIsLoaded = true;
      nextState.sentRecognitionsLoadFailed = true;
    }),
  )
  .handleAction(actions.getReviewRecognitions.success, (state, action) =>
    produce(state, (nextState) => {
      const { rows, count, is_clear } = action.payload;
      if (is_clear) {
        nextState.reviewRecognitions = [...rows];
      } else {
        // De-dupe by id so an append can never render a row twice: the review queue mutates at both
        // ends (rows leave as they're approved, and new recognitions default to Review and land at
        // the front), so any window a caller requests can legitimately overlap ids already held.
        // ReviewQueueContainer does not reach this branch today — it always requests page 0, which
        // takes the replace branch above — but this stays the generic contract for an append.
        const existingIds = new Set(nextState.reviewRecognitions.map((recognition) => recognition.id));
        nextState.reviewRecognitions = [
          ...nextState.reviewRecognitions,
          ...rows.filter((recognition) => !existingIds.has(recognition.id)),
        ];
      }
      nextState.reviewRecognitionsTotal = count;
      nextState.reviewRecognitionsIsLoaded = true;
    }),
  )
  // Without this handler a failed load leaves reviewRecognitionsIsLoaded false forever and the
  // container renders nothing at all — not even an error, since ErrorBoundary cannot catch a saga
  // rejection. Only flip isLoaded: do NOT touch reviewRecognitions/reviewRecognitionsTotal, so rows
  // already held survive a later failed refresh. (feed-failed-page-plan.md: the received/sent lists
  // now have their own handlers above, mirroring this one verbatim.)
  .handleAction(actions.getReviewRecognitions.failure, (state) =>
    produce(state, (nextState) => {
      nextState.reviewRecognitionsIsLoaded = true;
    }),
  )
  .handleAction(actions.getRecognition.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.recognition = action.payload;
    }),
  )
  .handleAction(actions.createRecognition.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.sentRecognitions = [action.payload.recognition, ...nextState.sentRecognitions];
      nextState.sentRecognitionsTotal += 1;
    }),
  )
  // This action fires from the same RecognitionStatusCell approve/decline buttons in both places
  // that component is rendered — the user-profile received-recognitions table (replace-in-place) and
  // the review queue table (remove-from-queue). Only the state the row happened to be in differs.
  //
  // Both branches MUST stay in this ONE handler. typesafe-actions' `reducerHandler` merges
  // registrations with `Object.assign` — no composition, no warning — so adding a second
  // `.handleAction(actions.updateRecognition.success, ...)` elsewhere would silently replace this
  // one and break the profile behaviour below with nothing to flag it.
  .handleAction(actions.updateRecognition.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.receivedRecognitions = nextState.receivedRecognitions.map((recognition) =>
        recognition.id === action.payload.recognition.id ? action.payload.recognition : recognition,
      );

      // Conditional on the id having actually BEEN in reviewRecognitions: this action also fires
      // from the user-profile table, where the row was never in the review queue. An unconditional
      // decrement (or one gated only on "is the queue non-empty") would drive reviewRecognitionsTotal
      // below truth and silently stop the queue's infinite scroll early.
      const wasInReviewQueue = nextState.reviewRecognitions.some(
        (recognition) => recognition.id === action.payload.recognition.id,
      );
      if (wasInReviewQueue) {
        nextState.reviewRecognitions = nextState.reviewRecognitions.filter(
          (recognition) => recognition.id !== action.payload.recognition.id,
        );
        nextState.reviewRecognitionsTotal -= 1;
      }
    }),
  )
  .handleAction(actions.getCoreValues.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.coreValues = action.payload;
      // A retry after a failure clears the message instead of showing it beside the catalog that just
      // landed -- mirrors the received/sent lists' own success/failure pair above.
      nextState.coreValuesLoadFailed = false;
    }),
  )
  // follow-up 111 / 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L5: the handler that never existed. Only
  // flip the flag -- do NOT touch coreValues, so a catalog already loaded (e.g. by an earlier screen
  // that shares this slice) survives a later failed refetch rather than being wiped to `[]`.
  .handleAction(actions.getCoreValues.failure, (state) =>
    produce(state, (nextState) => {
      nextState.coreValuesLoadFailed = true;
    }),
  )
  .handleAction(actions.getPendingCount.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.pendingCount = action.payload.count;
    }),
  )
  // Writes ONLY the two counts -- never rows, never a combined total -- so a later change to one
  // cannot silently corrupt the other (page-header-promotion-plan.md's own warning: this is the exact
  // defect the first design of this badge was rejected for, copying the list reducer's `rows` handling
  // into this handler).
  .handleAction(actions.getRecognitionCounts.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.recognitionCounts = action.payload;
    }),
  )

  // review B (C'): any write that resolves to page 0 -- an explicit reset (period/core-value-filter
  // changes) or the implicit one a null payload takes via `defaultFilter` on unmount -- resets the
  // landed-page counter too. The next request THIS filter produces requests page 0, so whatever was
  // counted before it must not survive to be read as "how many pages are already loaded" once it does.
  .handleAction(actions.setReceivedFilter, (state, action) =>
    produce(state, (nextState) => {
      const nextFilter = action.payload || { ...defaultFilter };
      nextState.receivedFilter = nextFilter;
      if (nextFilter.page === 0) {
        nextState.receivedRecognitionsLoadedPages = 0;
      }
    }),
  )
  .handleAction(actions.setSentFilter, (state, action) =>
    produce(state, (nextState) => {
      const nextFilter = action.payload || { ...defaultFilter };
      nextState.sentFilter = nextFilter;
      if (nextFilter.page === 0) {
        nextState.sentRecognitionsLoadedPages = 0;
      }
    }),
  )
  .handleAction(actions.setReviewFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.reviewFilter = action.payload || { ...defaultFilter };
    }),
  )

  .handleAction(actions.clearCoreValues, (state) =>
    produce(state, (nextState) => {
      nextState.coreValues = [];
      nextState.coreValuesLoadFailed = false;
    }),
  )

  // Deliberately does NOT touch pendingCount or recognitionCounts -- both are fetched by their own
  // sagas, independent of this list, and the page header (which reads recognitionCounts) persists
  // across tabs while this list's container does not. Zeroing either here would show a correct number
  // on Overview and 0 everywhere else the moment this list unmounts.
  .handleAction(actions.clearRecognitions, (state) =>
    produce(state, (nextState) => {
      nextState.receivedRecognitions = [];
      nextState.receivedRecognitionsTotal = 0;
      nextState.receivedRecognitionsIsLoaded = false;
      nextState.receivedRecognitionsLoadFailed = false;
      nextState.receivedRecognitionsLoadedPages = 0;
      nextState.sentRecognitions = [];
      nextState.sentRecognitionsTotal = 0;
      nextState.sentRecognitionsIsLoaded = false;
      nextState.sentRecognitionsLoadFailed = false;
      nextState.sentRecognitionsLoadedPages = 0;
    }),
  )
  // Deliberately separate from clearRecognitions above — two unrelated screens (received/sent
  // lists) dispatch that action, and extending it would clear the review queue's state whenever
  // either of those unmounts, unrelated to the review queue's own lifecycle.
  .handleAction(actions.clearReviewRecognitions, (state) =>
    produce(state, (nextState) => {
      nextState.reviewRecognitions = [];
      nextState.reviewRecognitionsTotal = 0;
      nextState.reviewRecognitionsIsLoaded = false;
    }),
  );

export { reducer as RecognitionReducer };
